import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { BookMarked, Check, ChevronDown, ChevronLeft, ChevronRight, Lock, Plus, Search, X } from 'lucide-react'
import { catalogApi } from '@/api/catalog'
import { vocabularyApi } from '@/api/vocabulary'
import { CefrBadge } from '@/components/CefrBadge'
import { EmptyState } from '@/components/EmptyState'
import { ErrorScreen } from '@/components/ErrorScreen'
import { SkeletonList } from '@/components/SkeletonList'
import { cn } from '@/components/ui/cn'
import { Input } from '@/components/ui/Input'
import { useDebounce } from '@/hooks/useDebounce'
import { formatTranslationPreview } from '@/lib/translationPreview'
import type { CefrLevel } from '@/types/common'
import type { CatalogUnit, LanguageUnitType } from '@/types/content'

const CEFR_OPTIONS: Array<{ value?: CefrLevel; label: string }> = [
  { label: 'All' },
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
]

const TYPE_OPTIONS: Array<{ value?: LanguageUnitType; label: string }> = [
  { label: 'All types' },
  { value: 'WORD', label: 'Words' },
  { value: 'PHRASE', label: 'Phrases' },
  { value: 'PHRASAL_VERB', label: 'Phrasal verbs' },
  { value: 'IDIOM', label: 'Idioms' },
]

const TYPE_COLORS: Record<string, string> = {
  WORD: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  PHRASE: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  PHRASAL_VERB: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  IDIOM: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  GRAMMAR_PATTERN: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
}

// Static category grouping — order determines sidebar order.
// Any category not in a group falls into "Other" at the bottom.
const CATEGORY_GROUPS: Array<{ label: string; cats: string[] }> = [
  { label: 'People & Society',  cats: ['Emotions', 'Personality', 'Relationships', 'Culture', 'Arts', 'Entertainment', 'Politics', 'Religion', 'Sports'] },
  { label: 'Daily Life',        cats: ['Everyday Life', 'Home', 'Family', 'Routines', 'Shopping', 'Money', 'Time'] },
  { label: 'Work & Career',     cats: ['Office', 'Business', 'Finance', 'Communication', 'Management', 'Law'] },
  { label: 'Health & Body',     cats: ['Emotions', 'Medicine', 'Fitness', 'Nutrition', 'Mental Health', 'Body'] },
  { label: 'Nature & World',    cats: ['Weather', 'Animals', 'Plants', 'Geography', 'Environment', 'Space'] },
  { label: 'Knowledge',         cats: ['School', 'University', 'Learning', 'Language', 'Science'] },
  { label: 'Food & Drink',      cats: ['Cooking', 'Restaurants', 'Ingredients', 'Beverages'] },
  { label: 'Technology',        cats: ['Computers', 'Internet', 'Devices', 'Programming', 'Engineering'] },
  { label: 'Travel & Transport', cats: ['Transport', 'Travel', 'Airport', 'Hotel', 'Directions', 'Documents', 'Tourism'] },
]

const PAGE_SIZE = 30

// freq values < 5000 are true COCA-style counts; larger values are rank indices
// from the CEFR-J source where we fall back to CEFR-derived labels.
function frequencyLabel(freq: number, cefrLevel?: string | null): string {
  if (freq < 5000) {
    if (freq >= 500) return 'Very common'
    if (freq >= 200) return 'Common'
    if (freq >= 80)  return 'Familiar'
    return ''
  }
  switch (cefrLevel) {
    case 'A1': return 'Very common'
    case 'A2': return 'Common'
    case 'B1': return 'Familiar'
    default:   return ''
  }
}

export function CatalogPage() {
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<string | undefined>()
  const [type, setType] = useState<LanguageUnitType | undefined>()
  const [cefr, setCefr] = useState<CefrLevel | undefined>()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [recentlyAddedSurfaces, setRecentlyAddedSurfaces] = useState<Set<string>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(CATEGORY_GROUPS.slice(0, 3).map((g) => g.label))
  )
  const [categorySheetOpen, setCategorySheetOpen] = useState(false)
  const [typeSheetOpen, setTypeSheetOpen] = useState(false)
  const [cefrSheetOpen, setCefrSheetOpen] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')

  const debouncedSearch = useDebounce(search, 300)

  const toggleGroup = (label: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      next.has(label) ? next.delete(label) : next.add(label)
      return next
    })

  // Auto-expand the group containing the active category
  const expandGroupFor = (cat: string) => {
    const group = CATEGORY_GROUPS.find((g) => g.cats.includes(cat))
    if (group) setExpandedGroups((prev) => new Set([...prev, group.label]))
  }

  const filters = useMemo(
    () => ({
      category,
      type,
      cefr,
      q: debouncedSearch.trim() || undefined,
      page,
      size: PAGE_SIZE,
    }),
    [category, type, cefr, debouncedSearch, page],
  )

  const { data: categories = [] } = useQuery({
    queryKey: ['catalogCategories'],
    queryFn: () => catalogApi.getCategories().then((r) => r.data),
  })

  // Map category name → CatalogCategoryResponse for fast lookup
  const categoryIndex = useMemo(
    () => new Map(categories.map((c) => [c.category, c])),
    [categories]
  )

  // Build grouped structure for sidebar (skip empty groups / categories with 0 count)
  const groupedCategories = useMemo(() => {
    const assigned = new Set<string>()
    const groups = CATEGORY_GROUPS.map((g) => {
      const items = g.cats
        .map((name) => categoryIndex.get(name))
        .filter((c): c is NonNullable<typeof c> => !!c && c.unitCount > 0)
      items.forEach((c) => assigned.add(c.category))
      return { label: g.label, items }
    }).filter((g) => g.items.length > 0)

    const ungrouped = categories.filter(
      (c) => !assigned.has(c.category) && c.unitCount > 0
    )
    return { groups, ungrouped }
  }, [categories, categoryIndex])

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['catalogUnits', filters],
    queryFn: () => catalogApi.getUnits(filters).then((r) => r.data),
    placeholderData: (prev) => prev,
  })

  const { data: knownForms = [] } = useQuery({
    queryKey: ['knownForms'],
    queryFn: () => vocabularyApi.getKnownForms().then((r) => r.data.forms),
  })

  const { data: knownPhrases } = useQuery({
    queryKey: ['knownPhrases'],
    queryFn: () => vocabularyApi.getKnownPhrases().then((r) => r.data),
  })

  const knownFormSet = useMemo(
    () => new Set(knownForms.map((form) => form.toLowerCase())),
    [knownForms],
  )

  const knownPhraseIdSet = useMemo(
    () => new Set((knownPhrases?.phraseIds ?? []).map((id) => id.toLowerCase())),
    [knownPhrases],
  )

  const addWordMutation = useMutation({
    mutationFn: (surface: string) => vocabularyApi.addWord({ word: surface }),
    onSuccess: (_response, surface) => {
      void queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      void queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      void queryClient.invalidateQueries({ queryKey: ['knownForms'] })
      setRecentlyAddedSurfaces((prev) => new Set([...prev, surface.toLowerCase()]))
    },
    onError: (error, surface) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['knownForms'] })
        setRecentlyAddedSurfaces((prev) => {
          const next = new Set(prev)
          next.delete(surface.toLowerCase())
          return next
        })
      }
    },
  })

  const addPhraseMutation = useMutation({
    mutationFn: (unit: CatalogUnit) => vocabularyApi.addPhrase({ phraseId: unit.id }),
    onSuccess: (_response, unit) => {
      void queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      void queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
      void queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      void queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
      setRecentlyAddedSurfaces((prev) => new Set([...prev, unit.surface.toLowerCase()]))
    },
    onError: (error, unit) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        // Already saved — treat as success for catalog CTA state.
        void queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
        void queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
        setRecentlyAddedSurfaces((prev) => new Set([...prev, unit.surface.toLowerCase()]))
        return
      }
      window.alert(
        isAxiosError(error)
          ? (error.response?.data?.message ?? 'Could not save phrase')
          : 'Could not save phrase',
      )
    },
  })

  const units = data?.content ?? []
  const totalPages = data?.totalPages ?? 0

  const resetPage = () => setPage(0)

  const handleAdd = (unit: CatalogUnit) => {
    if (unit.unitType === 'WORD') {
      addWordMutation.mutate(unit.surface)
      return
    }
    addPhraseMutation.mutate(unit)
  }

  const totalCategoryCount = categories.reduce((sum, c) => sum + c.unitCount, 0)

  const activeTypeLabel = TYPE_OPTIONS.find((o) => o.value === type)?.label ?? 'All types'
  const activeCefrLabel = CEFR_OPTIONS.find((o) => o.value === cefr)?.label ?? 'All levels'

  const selectCategory = (nextCategory: string | undefined) => {
    setCategory(nextCategory)
    resetPage()
    setCategorySheetOpen(false)
    setCategorySearch('')
    if (nextCategory) expandGroupFor(nextCategory)
  }

  const selectType = (nextType: LanguageUnitType | undefined) => {
    setType(nextType)
    resetPage()
    setTypeSheetOpen(false)
  }

  const selectCefr = (nextCefr: CefrLevel | undefined) => {
    setCefr(nextCefr)
    resetPage()
    setCefrSheetOpen(false)
  }

  const categorySearchLower = categorySearch.trim().toLowerCase()
  const categoryMatchesSearch = (label: string) =>
    !categorySearchLower || label.toLowerCase().includes(categorySearchLower)

  return (
    <div className="flex h-full min-h-0 gap-0">
      {/* ── Left sidebar: categories ────────────────────────────────── */}
      <aside
        className="hidden w-52 shrink-0 flex-col border-r border-border/50 pr-4 lg:flex"
        data-testid="catalog-desktop-sidebar"
      >
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Categories
        </p>
        <nav className="flex flex-col gap-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
          {/* All categories */}
          <CategoryButton
            active={!category}
            label="All categories"
            count={categories.reduce((sum, c) => sum + c.unitCount, 0)}
            onClick={() => { setCategory(undefined); resetPage() }}
          />

          <div className="my-2 border-t border-border/30" />

          {/* Grouped categories */}
          {groupedCategories.groups.map((group) => {
            const isExpanded = expandedGroups.has(group.label)
            const groupCount = group.items.reduce((s, c) => s + c.unitCount, 0)
            const hasActive = group.items.some((c) => c.category === category)
            return (
              <div key={group.label} className="mb-0.5">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
                    hasActive
                      ? 'text-primary'
                      : 'text-muted-foreground/60 hover:text-muted-foreground',
                  )}
                >
                  <span>{group.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="tabular-nums text-[10px]">{groupCount}</span>
                    {isExpanded
                      ? <ChevronDown className="h-3 w-3" />
                      : <ChevronRight className="h-3 w-3" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="ml-1 flex flex-col gap-0">
                    {group.items.map((item) => (
                      <CategoryButton
                        key={item.category}
                        active={category === item.category}
                        label={item.category}
                        count={item.unitCount}
                        onClick={() => { setCategory(item.category); resetPage() }}
                        indent
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          {/* Ungrouped leftovers */}
          {groupedCategories.ungrouped.length > 0 && (
            <>
              <div className="my-2 border-t border-border/30" />
              {groupedCategories.ungrouped.map((item) => (
                <CategoryButton
                  key={item.category}
                  active={category === item.category}
                  label={item.category}
                  count={item.unitCount}
                  onClick={() => { setCategory(item.category); resetPage() }}
                />
              ))}
            </>
          )}
        </nav>
      </aside>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col pl-0 lg:pl-6">

        {/* Header — compact on mobile */}
        <div className="mb-3 flex items-start justify-between gap-4 lg:mb-4">
          <div className="min-w-0 flex-1">
            <h1 className="hidden text-xl font-bold text-foreground lg:block">
              {category ?? 'Browse catalog'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {data?.totalElements != null
                ? `${data.totalElements} unit${data.totalElements === 1 ? '' : 's'}${category ? ` · ${category}` : ''}${debouncedSearch.trim() ? ` · matching “${debouncedSearch.trim()}”` : ''}`
                : 'Language Database — explore when you want to'}
            </p>
          </div>
        </div>

        {/* Search — paste-friendly; same chrome as Vocabulary */}
        <div className="relative mb-3 lg:mb-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className={cn('pl-9', search && 'pr-9')}
            placeholder="Paste a word or phrase…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            aria-label="Search catalog"
            data-testid="catalog-search"
          />
          {search ? (
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setPage(0)
              }}
              className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* Desktop filter bar */}
        <div className="mb-4 hidden flex-wrap items-center gap-x-5 gap-y-2 border-b border-border/40 pb-3 lg:flex">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Type</span>
            {TYPE_OPTIONS.map((o) => (
              <FilterPill
                key={o.label}
                active={type === o.value}
                label={o.label}
                onClick={() => { setType(o.value); resetPage() }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">CEFR</span>
            {CEFR_OPTIONS.map((o) => (
              <FilterPill
                key={o.label}
                active={cefr === o.value}
                label={o.label}
                onClick={() => { setCefr(o.value); resetPage() }}
              />
            ))}
          </div>
        </div>

        {/* Mobile filter triggers */}
        <div className="mb-4 flex flex-wrap gap-2 lg:hidden" data-testid="catalog-mobile-filters">
          <FilterTriggerButton
            label={category ?? 'All categories'}
            onClick={() => setCategorySheetOpen(true)}
            testId="catalog-filter-category"
          />
          <FilterTriggerButton
            label={activeTypeLabel}
            onClick={() => setTypeSheetOpen(true)}
            testId="catalog-filter-type"
          />
          <FilterTriggerButton
            label={activeCefrLabel}
            onClick={() => setCefrSheetOpen(true)}
            testId="catalog-filter-cefr"
          />
        </div>

        {/* List */}
        {isLoading ? (
          <SkeletonList rows={8} />
        ) : isError ? (
          <ErrorScreen
            variant="network"
            title="Couldn't load the catalog"
            description="Please check your connection and try again."
            onRetry={() => refetch()}
          />
        ) : units.length === 0 ? (
          <EmptyState
            title={debouncedSearch.trim() ? 'No matches' : 'No units match these filters'}
            description={
              debouncedSearch.trim()
                ? `Nothing close to “${debouncedSearch.trim()}”. Try fewer words or check spelling.`
                : 'Try a different category, type, or CEFR level.'
            }
          />
        ) : (
          <div
            className="flex min-h-0 flex-col"
            data-testid="catalog-list-panel"
          >
            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto transition-opacity',
                'max-lg:space-y-2 max-lg:pb-14',
                'lg:divide-y lg:divide-border/40 lg:rounded-xl lg:border lg:border-border/50 lg:bg-card/30',
                isFetching && 'opacity-60',
              )}
              data-testid="catalog-unit-list"
            >
              {units.map((unit) => {
                const canAdd = true
                const surfaceKey = unit.surface.toLowerCase()
                const isWord = unit.unitType === 'WORD'
                const isSaving = isWord
                  ? addWordMutation.isPending && addWordMutation.variables === unit.surface
                  : addPhraseMutation.isPending && addPhraseMutation.variables?.id === unit.id
                const justAdded = recentlyAddedSurfaces.has(surfaceKey)
                const inDictionary = isWord
                  ? knownFormSet.has(surfaceKey)
                  : knownPhraseIdSet.has(unit.id.toLowerCase())
                const typeColor = TYPE_COLORS[unit.unitType] ?? TYPE_COLORS.WORD

                return (
                  <div
                    key={unit.id}
                    className={cn(
                      'group transition-colors',
                      'max-lg:rounded-xl max-lg:border max-lg:border-border/50 max-lg:bg-card/60 max-lg:p-3',
                      'lg:flex lg:items-center lg:gap-4 lg:px-4 lg:py-3 lg:hover:bg-accent/30',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3 lg:contents">
                      {/* Type badge — desktop only */}
                      <span
                        className={cn(
                          'hidden w-20 shrink-0 rounded-md border px-1.5 py-0.5 text-center text-[10px] font-medium uppercase tracking-wide sm:block',
                          typeColor,
                        )}
                      >
                        {unit.unitType.replace(/_/g, ' ').toLowerCase()}
                      </span>

                      {/* Surface + meta */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-semibold text-foreground">{unit.surface}</span>
                          {unit.partOfSpeech && (
                            <span className="text-xs italic text-muted-foreground">{unit.partOfSpeech}</span>
                          )}
                        </div>
                        {(unit.previewTranslations?.length ?? 0) > 0 && (
                          <p
                            className="mt-0.5 line-clamp-2 text-sm text-muted-foreground lg:truncate"
                            title={formatTranslationPreview(
                              unit.previewTranslations!,
                              2,
                              unit.totalTranslationCount,
                            )}
                          >
                            {formatTranslationPreview(
                              unit.previewTranslations!,
                              2,
                              unit.totalTranslationCount,
                            )}
                          </p>
                        )}
                        {unit.categories.length > 0 && (
                          <p className="mt-0.5 text-[11px] text-muted-foreground/60">
                            {unit.categories.join(' · ')}
                          </p>
                        )}
                      </div>

                      {/* Badges + CTA */}
                      <div className="flex shrink-0 flex-col items-end gap-2 lg:flex-row lg:items-center">
                        <div className="flex items-center gap-2">
                          {unit.cefrLevel && <CefrBadge level={unit.cefrLevel} />}
                          {typeof unit.frequency === 'number' && frequencyLabel(unit.frequency, unit.cefrLevel) && (
                            <span className="hidden text-[11px] text-muted-foreground/50 sm:block">
                              {frequencyLabel(unit.frequency, unit.cefrLevel)}
                            </span>
                          )}
                        </div>

                        <div className="lg:w-36 lg:text-right">
                          {justAdded ? (
                            <span className="inline-flex min-h-11 items-center gap-1 text-xs text-emerald-500 lg:min-h-0">
                              <Check className="h-3.5 w-3.5" />
                              Added
                            </span>
                          ) : inDictionary ? (
                            <span
                              className="inline-flex min-h-11 items-center gap-1 text-xs text-muted-foreground lg:min-h-0"
                              title="This word is already in your dictionary"
                            >
                              <BookMarked className="h-3.5 w-3.5" />
                              <span className="max-lg:hidden">In your dictionary</span>
                              <span className="lg:hidden">Saved</span>
                            </span>
                          ) : canAdd ? (
                            <button
                              type="button"
                              onClick={() => handleAdd(unit)}
                              disabled={isSaving}
                              className={cn(
                                'inline-flex items-center gap-1 rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-medium text-primary transition-opacity hover:bg-primary/10 disabled:opacity-40',
                                'max-lg:min-h-11 max-lg:px-4 max-lg:opacity-100',
                                'lg:opacity-0 lg:group-hover:opacity-100',
                              )}
                            >
                              {isSaving ? 'Saving…' : (
                                <>
                                  <Plus className="h-3 w-3" />
                                  Add
                                </>
                              )}
                            </button>
                          ) : (
                            <span
                              className="hidden items-center gap-1 text-[11px] text-muted-foreground/40 sm:inline-flex"
                              title="Learning support coming soon"
                            >
                              <Lock className="h-3 w-3" />
                              Coming soon
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div
                className={cn(
                  'flex shrink-0 items-center justify-center gap-2 border-t border-border/40 bg-background px-2 py-1.5',
                  'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-16 max-lg:z-20 max-lg:border-border',
                  'lg:mt-4 lg:gap-3 lg:border-t-0 lg:bg-transparent lg:px-0 lg:py-0',
                )}
                data-testid="catalog-pagination"
              >
                <button
                  type="button"
                  className="inline-flex min-h-8 items-center gap-0.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40 lg:min-h-11 lg:gap-1 lg:px-3 lg:text-sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-3.5 w-3.5 lg:h-4 lg:w-4" /> Prev
                </button>
                <span className="min-w-14 text-center text-xs tabular-nums text-muted-foreground lg:min-w-16 lg:text-sm">
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="inline-flex min-h-8 items-center gap-0.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40 lg:min-h-11 lg:gap-1 lg:px-3 lg:text-sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next <ChevronRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <CatalogBottomSheet
        open={categorySheetOpen}
        title="Category"
        onClose={() => {
          setCategorySheetOpen(false)
          setCategorySearch('')
        }}
        testId="catalog-category-sheet"
      >
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            placeholder="Search categories…"
            className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="catalog-category-search"
          />
        </div>

        <CategoryButton
          active={!category}
          label="All categories"
          count={totalCategoryCount}
          onClick={() => selectCategory(undefined)}
        />

        <div className="my-2 border-t border-border/30" />

        {groupedCategories.groups.map((group) => {
          const visibleItems = group.items.filter((item) => categoryMatchesSearch(item.category))
          const groupVisible = categoryMatchesSearch(group.label) || visibleItems.length > 0
          if (!groupVisible) return null

          const isExpanded = expandedGroups.has(group.label) || Boolean(categorySearchLower)
          const groupCount = visibleItems.reduce((s, c) => s + c.unitCount, 0)
          const hasActive = visibleItems.some((c) => c.category === category)

          return (
            <div key={group.label} className="mb-0.5">
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                className={cn(
                  'flex min-h-11 w-full items-center justify-between rounded-md px-2 py-2 text-xs font-semibold uppercase tracking-wide transition-colors',
                  hasActive ? 'text-primary' : 'text-muted-foreground/60 hover:text-muted-foreground',
                )}
              >
                <span>{group.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className="tabular-nums text-[10px]">{groupCount}</span>
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </div>
              </button>
              {isExpanded && (
                <div className="ml-1 flex flex-col gap-0">
                  {visibleItems.map((item) => (
                    <CategoryButton
                      key={item.category}
                      active={category === item.category}
                      label={item.category}
                      count={item.unitCount}
                      onClick={() => selectCategory(item.category)}
                      indent
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {groupedCategories.ungrouped.filter((item) => categoryMatchesSearch(item.category)).length > 0 && (
          <>
            <div className="my-2 border-t border-border/30" />
            {groupedCategories.ungrouped
              .filter((item) => categoryMatchesSearch(item.category))
              .map((item) => (
                <CategoryButton
                  key={item.category}
                  active={category === item.category}
                  label={item.category}
                  count={item.unitCount}
                  onClick={() => selectCategory(item.category)}
                />
              ))}
          </>
        )}
      </CatalogBottomSheet>

      <CatalogBottomSheet
        open={typeSheetOpen}
        title="Type"
        onClose={() => setTypeSheetOpen(false)}
        testId="catalog-type-sheet"
      >
        {TYPE_OPTIONS.map((o) => (
          <SheetOptionButton
            key={o.label}
            active={type === o.value}
            label={o.label}
            onClick={() => selectType(o.value)}
          />
        ))}
      </CatalogBottomSheet>

      <CatalogBottomSheet
        open={cefrSheetOpen}
        title="CEFR level"
        onClose={() => setCefrSheetOpen(false)}
        testId="catalog-cefr-sheet"
      >
        {CEFR_OPTIONS.map((o) => (
          <SheetOptionButton
            key={o.label}
            active={cefr === o.value}
            label={o.label}
            onClick={() => selectCefr(o.value)}
          />
        ))}
      </CatalogBottomSheet>
    </div>
  )
}

function FilterTriggerButton({
  label,
  onClick,
  testId,
}: {
  label: string
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
    >
      <span className="truncate">{label}</span>
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  )
}

function CatalogBottomSheet({
  open,
  title,
  onClose,
  testId,
  children,
}: {
  open: boolean
  title: string
  onClose: () => void
  testId: string
  children: ReactNode
}) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" data-testid={testId}>
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="absolute bottom-0 left-0 right-0 flex max-h-[75dvh] flex-col rounded-t-2xl border-t border-border bg-card shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>
  )
}

function SheetOptionButton({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'mb-1 flex min-h-11 w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors',
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-foreground hover:bg-muted',
      )}
    >
      <span>{label}</span>
      {active ? <Check className="h-4 w-4" /> : null}
    </button>
  )
}

function CategoryButton({
  active,
  label,
  count,
  onClick,
  compact = false,
  indent = false,
}: {
  active: boolean
  label: string
  count: number
  onClick: () => void
  compact?: boolean
  indent?: boolean
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'rounded-full border px-3 py-1 text-sm transition-colors',
          active
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        {label}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded-lg py-2 text-sm transition-colors',
        indent ? 'pl-4 pr-2' : 'px-3',
        'min-h-11',
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <span className="truncate">{label}</span>
      <span className={cn('ml-2 shrink-0 text-xs tabular-nums', active ? 'text-primary/70' : 'text-muted-foreground/50')}>
        {count}
      </span>
    </button>
  )
}

function FilterPill({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-2.5 py-0.5 text-xs transition-colors',
        active
          ? 'border-primary bg-primary/10 font-medium text-primary'
          : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
}
